import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errorText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ago, clock, useDash } from "../../lib/dash";
import { Tile, TileState, Chip } from "./Tile";
import { AdsWindows } from "./AdsWindows";
import { AdsDailyChart } from "./AdsDailyChart";
import { AdsCampaigns } from "./AdsCampaigns";
import { AdsFacts } from "./AdsFacts";

const STATE = { ok: "Connected", waiting: "Connected · no numbers yet", not_connected: "Not connected", rejected: "Token rejected",
  throttled: "Meta slowed us down", no_access: "No access", too_broad: "Token too broad", no_answer: "No answer", error: "Meta error" };

const refreshed = (data) => {
  const r = data.refresh || {};
  if (r.state === "ok") return "Refreshed from Meta";
  if (r.skipped === "not due") return `Next refresh allowed at ${clock(data.refresh_allowed_at)}`;
  if (r.skipped === "not connected") return "Not connected: nothing to refresh";
  return r.started ? "Refresh started" : STATE[r.state] || "Done";
};

const NotConnected = ({ d }) => (
  <div data-testid="ads-not-connected" className="grid flex-1 place-items-center rounded-xl border border-dashed border-border px-4 py-6 text-center">
    <div className="max-w-xl space-y-1.5">
      <p data-testid="ads-message" className="text-[13px] font-semibold">{d.message}</p>
      {d.preview ? (
        <p data-testid="ads-preview-note" className="text-[12px] text-muted-foreground">
          This is the preview: it never calls Meta, even when a token is set, and keeps no token date. Your real numbers show on the live site.
        </p>
      ) : (
        <>
          <p className="text-[12px] text-muted-foreground">
            No call is made to Meta until both are set (in the secrets card; never in chat). The setup steps (Business app, a System User,
            View performance on the ad account, ads_read only, expiry Never) are in memory/plans/META_TOKEN_SETUP.md.
          </p>
          <p className="text-[11px] text-muted-foreground">Optional: META_FINGERPRINT_KEY, so the tile can show "Token set on" and remind you yearly.</p>
        </>
      )}
    </div>
  </div>
);

const Banner = ({ d }) => d.message && (
  <p data-testid="ads-message" className={`mb-2 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold ${d.tone === "red" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-800"}`}>
    {d.message}{d.error?.code ? ` · Meta error ${d.error.code}${d.error.subcode ? `/${d.error.subcode}` : ""} · trace ${d.error.trace || "-"}` : ""}
    {d.stale && d.refreshed_at ? ` · showing the numbers as of ${clock(d.refreshed_at)}` : ""}
  </p>
);

const Figures = ({ d, owner, onTokenDate }) => (
  <>
    <Banner d={d} />
    {d.windows.length === 0 ? (
      <p data-testid="ads-waiting" className="grid flex-1 place-items-center text-[12px] text-muted-foreground">No numbers yet. They arrive with the next refresh.</p>
    ) : (
      <div data-testid="ads-figures" className={`grid min-h-0 flex-1 gap-4 lg:grid-cols-12 lg:grid-rows-[minmax(0,1fr)] ${d.stale ? "opacity-55 grayscale" : ""}`}>
        <div className="flex min-h-0 flex-col lg:col-span-5"><AdsWindows wins={d.windows} /></div>
        <div className="flex min-h-0 lg:col-span-4"><div className="flex min-h-0 w-full flex-col"><AdsDailyChart daily={d.daily} /></div></div>
        <div className="flex min-h-0 lg:col-span-3"><div className="flex min-h-0 w-full flex-col"><AdsCampaigns wins={d.windows} /></div></div>
      </div>
    )}
    <AdsFacts d={d} owner={owner} onTokenDate={onTokenDate} />
  </>
);

// Tile 13 (plan 4e): Facebook ad spend, read from the server's cache (Meta is asked at most every 30-60 min).
export const AdsTile = ({ index }) => {
  const s = useDash("/dash/ads");
  const { user } = useAuth();
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => setSaved(null), [s.data]);
  const d = saved || s.data;
  const send = useCallback(async (method, url, body, ok) => {
    setBusy(true);
    try {
      const { data } = await api[method](url, body);
      setSaved(data);
      toast.success(ok(data));
    } catch (e) {
      toast.error(errorText(e, "Could not reach the server."));
    }
    setBusy(false);
  }, []);
  const waitUntil = d?.refresh_allowed_at && new Date(d.refresh_allowed_at) > new Date() ? d.refresh_allowed_at : null;
  const action = d && (
    <span className="flex shrink-0 items-center gap-2">
      <Chip testid="ads-state" tone={d.tone || (d.state === "ok" ? "green" : undefined)}>
        {STATE[d.state] || d.state}{d.state === "ok" && d.refreshed_at ? ` · updated ${ago(d.refreshed_at)}` : ""}
      </Chip>
      {d.state !== "not_connected" && (
        <button type="button" data-testid="ads-refresh" disabled={busy || !!waitUntil} onClick={() => send("post", "/dash/ads/refresh", null, refreshed)}
          title={waitUntil ? `At most one Meta refresh every ${d.floor_minutes} min: next at ${clock(waitUntil)}` : "Ask Meta now (logged)"}
          className="inline-flex h-7 items-center gap-1 rounded-full border border-border px-2.5 text-[11px] font-semibold transition-colors hover:bg-muted disabled:opacity-45">
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh now
        </button>
      )}
    </span>
  );
  return (
    <Tile id="ads" index={index} tone={d?.tone} action={action} title="Facebook ad spend"
      kicker={`Tile 13 · Meta Marketing API ${d?.version?.pinned || ""} · read-only · ${d ? `every ${d.every_minutes} min` : ""}`}>
      <TileState state={s}>
        {d && (d.state === "not_connected" ? <NotConnected d={d} /> : (
          <Figures d={d} owner={user?.role === "owner"} onTokenDate={(date) => send("put", "/dash/ads/token-date", { date }, () => "Token date corrected (logged)")} />
        ))}
      </TileState>
    </Tile>
  );
};
